import { formatDuration as dateFnsFormatDuration } from 'date-fns/formatDuration'
import { intervalToDuration } from 'date-fns/intervalToDuration'

import type { Language } from '../i18n'
import { dateLocales } from '../i18n/date-locales'

export type DurationLiteral = 'ms' | 's' | 'm' | 'h' | 'd'
export type DurationType = `${number}${DurationLiteral}`
export type Duration<T extends DurationType = DurationType> = T

export function toSeconds(value: Duration): number {
    const [num, lit] = parseDuration(value)

    switch (lit) {
        case 'ms':
            return num / 1000
        case 's':
            return num
        case 'm':
            return num * 60
        case 'h':
            return num * 60 * 60
        case 'd':
            return num * 60 * 60 * 24
        default:
            throw new Error(`Invalid duration literal: ${lit}`)
    }
}

export function toMinutes(value: Duration): number {
    const seconds = toSeconds(value)
    return seconds / 60
}

export function toHours(value: Duration): number {
    const seconds = toSeconds(value)
    return seconds / 60 / 60
}

export function toDays(value: Duration): number {
    const seconds = toSeconds(value)
    return seconds / 60 / 60 / 24
}

export function fromSeconds(value: number): Duration {
    return `${value}s` as Duration
}

export function parseDuration(value: Duration): [number, DurationLiteral] {
    const [unparsed, numstr, litstr] = value.split(durationRegexp)
    if (unparsed) {
        throw new Error(`Invalid duration: ${value}`)
    }

    const num = Number(numstr)
    const lit = litstr as DurationLiteral

    return [num, lit]
}

export function isDuration(value: unknown): value is Duration {
    return typeof value === 'string' ? durationRegexp.test(value) : false
}

export function isDurationEqual(a: Duration | null, b: Duration | null): boolean {
    if (a === null || b === null) {
        return a === b
    }
    return toSeconds(a) === toSeconds(b)
}

export function formatDuration(duration: Duration, lang: Language): string {
    return dateFnsFormatDuration(intervalToDuration({ start: 0, end: toSeconds(duration) * 1000 }), {
        locale: dateLocales[lang],
    })
}

const durationRegexp = /^(\d+)(ms|s|m|h|d)$/u
